import React, { useEffect, useState, useRef } from 'react';
import _ from 'underscore';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { InteractivePropType } from 'propTypes/common';
import { getIsNumberInRange } from 'utils/helpers/number';
import useIsAuthorized from 'hooks/useIsAuthorized';

import CustomSelect from 'components/filters/CustomSelect';

import { logError } from 'utils/logger';
import { fulfillInteractive } from '../components/helpers';
import DifficultyLevel from '../components/DifficultyLevel';
// eslint-disable-next-line import/no-cycle
import QuestionsList from '../components/QuestionsList';
import SectionResults from '../components/SectionResults';

import styles from './styles.module.scss';

const TestCorrect = ({ interactive, initialScore, initialDifficulty }) => {
	const { difficulties, mode, questions, results, type } = interactive;

	const [activeLevel, setActiveLevel] = useState(null);
	const [activeQuestions, setActiveQuestions] = useState([]);
	const [showQuestions, setShowQuestions] = useState(true);
	const [actualDifficulties, setActualDifficulties] = useState();
	const [score, setScore] = useState(initialScore || 0);
	const [resultMetadata, setResultMetadata] = useState(null);
	const [isTestFinished, setIsTestFinished] = useState(false);
	const [isRestarted, setIsRestarted] = useState(false);

	const isSliderLayout = mode === 'slider';
	const contentContainerRef = useRef(null);

	const hasDifficultQuestions = !_.isEmpty(difficulties);

	const grouppedQuestionByDifficulty = hasDifficultQuestions
		? _.groupBy(questions, (question) => question.difficult)
		: null;
	const grouppedQuestionByResults = hasDifficultQuestions
		? _.groupBy(results, (result) => result.difficult)
		: null;

	const [questionsAmount, setQuestionsAmount] = useState(
		hasDifficultQuestions ? 0 : questions.length
	);

	const [selectedVariants, setSelectedVariants] = useState(
		Array(questionsAmount).fill({})
	);

	const checkAlreadyCompleted = () => {
		const existInitialScore =
			_.isNumber(initialScore) &&
			interactive.results.some((result) =>
				getIsNumberInRange(initialScore, result.range.from, result.range.to)
			);

		if (existInitialScore && hasDifficultQuestions) {
			const existInitialDifficulty =
				initialDifficulty &&
				interactive?.difficulties.some(
					(difficulty) => difficulty.name === initialDifficulty
				);

			return existInitialDifficulty && _.isNumber(initialScore);
		}

		return existInitialScore;
	};

	const isAlreadyCompleted = checkAlreadyCompleted();

	const {
		query: { _id },
		reload,
		replace,
		asPath
	} = useRouter();

	const { isAuthorized } = useIsAuthorized();

	const selectVariant = (variant, index) => {
		setScore((prevValue) => prevValue + (variant.isCorrect ? 1 : 0));
		setSelectedVariants((prevValues) => {
			const tempPrevValues = [...prevValues];
			tempPrevValues[index] = variant;

			return tempPrevValues;
		});
	};

	const finishTest = () => {
		if (mode === 'slider') {
			setIsTestFinished(true);
		}
	};

	const selectLevel = (levelName) => {
		if (hasDifficultQuestions) {
			setActiveLevel(levelName);
			setActiveQuestions(grouppedQuestionByDifficulty[levelName]);
			setQuestionsAmount(grouppedQuestionByDifficulty[levelName].length);
			setShowQuestions(true);
		}
	};

	const restartTest = (level) => {
		setIsRestarted(true);
		setIsTestFinished(false);
		setScore(0);
		setSelectedVariants(Array(questionsAmount).fill({}));
		replace(asPath, undefined, { scroll: true });
		setResultMetadata(null);
		setActiveLevel(level || null);

		if (level) {
			const levelQuestions = grouppedQuestionByDifficulty[level];

			setActiveQuestions(levelQuestions);
			setQuestionsAmount(levelQuestions?.length);
		} else {
			setQuestionsAmount(questions?.length);
			setActiveQuestions(hasDifficultQuestions ? [] : questions);
		}
	};

	const switchLevel = (levelValue) => {
		if (levelValue === activeLevel) {
			return;
		}
		restartTest(levelValue);
	};

	const checkShowQuestion = () => {
		const show =
			(!isAlreadyCompleted && !isTestFinished) ||
			isRestarted ||
			(isTestFinished && mode === 'list');

		if (hasDifficultQuestions) {
			return !!activeLevel && show;
		}

		return show;
	};

	const getResults = (levelName) => {
		if (hasDifficultQuestions) {
			return grouppedQuestionByResults[levelName];
		}

		return results;
	};

	useEffect(() => {
		if (!hasDifficultQuestions) {
			setActiveQuestions(questions);
			setQuestionsAmount(questions.length);
		}

		if (hasDifficultQuestions && !_.isEmpty(resultMetadata)) {
			setQuestionsAmount(
				grouppedQuestionByDifficulty[resultMetadata?.difficult]?.length
			);
		}

		setShowQuestions(checkShowQuestion());
	}, [activeQuestions, resultMetadata]);

	useEffect(() => {
		if (hasDifficultQuestions) {
			const filterDifficulties = difficulties?.filter((difficulty) =>
				questions?.find((question) => question.difficult === difficulty.name)
			);

			setActualDifficulties(filterDifficulties);
		} else {
			setActiveQuestions(questions);
			setQuestionsAmount(questions.length);
		}

		if (isAlreadyCompleted) {
			const prepareResults = getResults(initialDifficulty);

			const metadata = prepareResults?.find((res) =>
				getIsNumberInRange(initialScore, res.range.from, res.range.to)
			);

			setResultMetadata(metadata);
		}
		window.history.scrollRestoration = 'manual';
	}, []);

	useEffect(() => {
		const isFinished = isTestFinished;
		const isNonDifficultyFinished =
			mode === 'list' &&
			!hasDifficultQuestions &&
			selectedVariants.filter((item) => !_.isEmpty(item)).length ===
				questionsAmount;

		const isDifficultyFinished =
			mode === 'list' &&
			hasDifficultQuestions &&
			activeLevel &&
			selectedVariants.filter((item) => !_.isEmpty(item)).length ===
				questionsAmount;
		setIsTestFinished(
			isFinished || isNonDifficultyFinished || isDifficultyFinished
		);
	}, [selectedVariants]);

	useEffect(() => {
		if (isTestFinished) {
			const prepareResults = getResults(activeLevel);
			const metadata = prepareResults?.find((res) =>
				getIsNumberInRange(score, res.range.from, res.range.to)
			);

			if (!metadata) {
				logError('No result for this question');
				restartTest();
				return;
			}
			setIsRestarted(false);
			setResultMetadata(metadata);
			if (isAuthorized) {
				fulfillInteractive(
					{ score, difficulty: activeLevel },
					interactive._id,
					_id
				);
			}
		}
	}, [isTestFinished]);

	const showResult = !_.isEmpty(resultMetadata) && !isRestarted;

	const showDifficulties =
		!_.isEmpty(actualDifficulties) && showQuestions === false && !showResult;

	const difficultiesOptions = actualDifficulties?.map((difficulty) => ({
		title: difficulty.title,
		name: difficulty.name
	}));

	return (
		<div className={styles.Container} ref={contentContainerRef}>
			{showDifficulties && (
				<DifficultyLevel
					levels={actualDifficulties}
					selectLevel={selectLevel}
				/>
			)}

			{showQuestions && (
				<>
					{!_.isEmpty(difficultiesOptions) && activeLevel && (
						<div className={styles.QuestionsSwitchDifficulty}>
							Сложность:
							<CustomSelect
								name="selectDifficulties"
								options={difficultiesOptions}
								selectedValue={activeLevel}
								onChange={(name, value) => switchLevel(value)}
								resetOption={null}
							/>
						</div>
					)}

					<QuestionsList
						{...{
							contentContainerRef,
							finishTest,
							isSliderLayout,
							isTestFinished,
							isTestCorrect: true,
							mode,
							questions: activeQuestions,
							selectVariant,
							selectedVariants,
							type
						}}
					/>
				</>
			)}

			{showResult && (
				<SectionResults
					{...{
						score,
						questionsAmount,
						resultMetadata,
						showCorrectAnswersAmount: true
					}}
					restartTest={isAuthorized ? () => restartTest() : reload}
				/>
			)}
		</div>
	);
};

TestCorrect.propTypes = {
	interactive: InteractivePropType,
	initialScore: PropTypes.number,
	initialDifficulty: PropTypes.string
};

export default TestCorrect;
